import { useCallback, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@scio3/ui';
import { APP_TYPES } from '@scio3/shared-types';
import type { AppMeta } from '@scio3/shared-types';
import { SafeHtml } from '@/components/shared/SafeHtml';
import { usePromotionalBanner } from '@/hooks/usePromotionalBanner';
import { useSubscriptionModal } from '@/hooks/useSubscriptionModal';
import { analyticsUpgradeCta } from '@/lib/analytics-service';
import { useAuthStore } from '@/stores/auth-store';
import { useSmallAppEditorStore } from '@/stores/smallapp-editor-store';

/**
 * If the promo HTML contains a single <a> wrapping all text
 * (e.g. "<a href='...'>Plain text. CTA!</a>"), pull the plain text outside the
 * link so only the last sentence (the CTA like "Explore Now!") is underlined.
 */
/** Upgrade CTA button — identical across trial and free/paid banners. */
function UpgradeButton({
  upgrading,
  onClick,
}: {
  upgrading: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  const { t } = useTranslation();
  return (
    <Button
      size="sm"
      className="hidden md:inline-flex h-8 shrink-0 rounded-md bg-primary px-5 text-xs font-semibold text-white hover:bg-primary/90"
      onClick={onClick}
      disabled={upgrading}
    >
      {upgrading ? (
        <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
      ) : (
        <svg
          className="mr-1.5 h-4 w-4 shrink-0"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          aria-hidden="true"
        >
          <path
            d="M4.054,15.198C8.345,7.768 13.578,3.263 20.928,2.014C21.073,1.988 21.222,1.998 21.362,2.042C21.502,2.086 21.629,2.163 21.733,2.267C21.837,2.371 21.914,2.498 21.959,2.639C22.003,2.779 22.012,2.928 21.986,3.072C20.737,10.422 16.232,15.655 8.802,19.946L4.054,15.198ZM3.819,17.337L2.633,20.448C2.584,20.577 2.574,20.716 2.602,20.851C2.631,20.985 2.698,21.108 2.795,21.205C2.892,21.302 3.015,21.369 3.15,21.398C3.284,21.427 3.424,21.416 3.552,21.367L6.665,20.18L3.819,17.337ZM16.5,5.998C16.204,5.998 15.914,6.086 15.667,6.251C15.42,6.416 15.228,6.65 15.114,6.924C15.001,7.198 14.971,7.5 15.029,7.791C15.087,8.082 15.23,8.349 15.44,8.559C15.649,8.769 15.917,8.911 16.208,8.969C16.499,9.027 16.8,8.998 17.074,8.884C17.348,8.77 17.583,8.578 17.747,8.332C17.912,8.085 18,7.795 18,7.498C18,7.1 17.842,6.719 17.561,6.438C17.28,6.156 16.898,5.998 16.5,5.998Z"
            fill="#fff"
            fillOpacity="0.9"
          />
          <use
            xlinkHref="#_BannerRocketImage"
            x="2.042"
            y="8.465"
            width="13.709"
            height="13.709"
            transform="matrix(0.979229,0,0,0.979231,0,0)"
          />
          <defs>
            <image
              id="_BannerRocketImage"
              width="14"
              height="14"
              xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAA4AAAAOCAYAAAAfSC3RAAAACXBIWXMAAA7EAAAOxAGVKw4bAAABDklEQVQokY3RLUtnURDH8c+ID82uIGzaLJosYjlvQBebgk2DCGoRrIJF0WYTtO52YZJbDPomtvgCtAl6N3iuXC9/HwYOzJmZ7/zOmQkDrCllHAu4isynQTXDPWAEv+rZ/Ah6A5tSAvPYwgQ2IvO+1zQis2nv0ZTyE7uYqbHDyPzdAUZxiB9YauFhnGG81t3hTw86whyuu4pDOK/+Ew7aZA+6wV736VGLVtFE5uUH0E53UE0po9H//DegScy+gTU4hNNPoGnsY+3dHiPzpSnlFg12W6iua9Hr9E8i8/GdYlc5Ml+qP1VVZvEPy5H5PBDsNFjBOsZqaDsy//K6js8sOtBdC30JRuYFTvCA427uPzZdckSDC+gbAAAAAElFTkSuQmCC"
            />
          </defs>
        </svg>
      )}
      {t('Upgrade now')}
    </Button>
  );
}

export function splitPromoLink(html: string): string {
  // Match an <a ...>...</a> anywhere in the string, capturing the opening tag and inner text
  const match = html.match(/(<a\s[^]*?>)([^]*?)<\/a>/i);
  if (!match) return html;

  const fullMatch = match[0]; // full <a ...>...</a>
  const aOpen = match[1]; // <a href="...">
  const inner = match[2]; // inner text content
  const lastDotSpace = inner.lastIndexOf('. ');

  if (lastDotSpace === -1) return html;

  const plainPart = inner.slice(0, lastDotSpace + 2);
  const ctaPart = inner.slice(lastDotSpace + 2);

  return html.replace(fullMatch, `${plainPart}${aOpen}${ctaPart}</a>`);
}

export function PromotionalBanner() {
  const { t } = useTranslation();
  const { isVisible, bannerType, bannerHtml, daysLeft, showUpgrade, dismiss } =
    usePromotionalBanner();
  const { openSubscriptionPlan } = useSubscriptionModal();
  const company = useAuthStore((s) => s.company);
  const me = useAuthStore((s) => s.me);
  const [upgrading, setUpgrading] = useState(false);

  /** Delegate clicks on banner HTML elements by id (matches scio1 dashboard.js onBannerClick) */
  const handleBannerClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = (e.target as HTMLElement).closest('[id]');
      if (!target) return;

      if (target.id === 'openBackgroundMusic') {
        e.preventDefault();
        e.stopPropagation();
        const appMeta = me?.appMetas?.find(
          (m: { appType?: string }) => m.appType === APP_TYPES.BACKGROUND_MUSIC,
        );
        if (!appMeta) return;
        useSmallAppEditorStore.getState().openEditor({
          appMeta: appMeta as AppMeta,
          asset: { appType: APP_TYPES.BACKGROUND_MUSIC },
          options: { isCloseModalAfterSave: true },
        });
      }
    },
    [me],
  );

  if (!isVisible) return null;
  if (bannerType !== 'trial' && !bannerHtml) return null;

  const handleUpgrade = async () => {
    if (upgrading) return;
    setUpgrading(true);
    analyticsUpgradeCta({
      ctaLocation: `promotional_banner_${bannerType}`,
      currentPlan: company?.payment?.plan,
    });
    try {
      // No fixed delay needed — scio1 subscriptionPlanModal now subscribes to
      // 'myCompany' reactively + showStep() guards against null data (#718).
      await openSubscriptionPlan({
        // Proactive entry — user clicked Upgrade Now in the banner, not feature-gated.
        // Override the wizard's default "Upgrade Required" title with friendlier scio1 wording.
        // Use `wizardTitle` (NOT `title`) so feature-gate callers that still pass
        // `title: t('Change Plan')` for the legacy iframe path are unaffected.
        wizardTitle: t('Select Your Plan'),
        // Match scio1 dashboard.js openSubscriptionPlanModal: use changePlanTrialToPay for
        // both trial and free plan users from the banner. changePlanPay triggers reactive
        // billing email validation in subscriptionPlanModal.js for accounts without a card.
        action: bannerType === 'paid' ? 'changePlanPay' : 'changePlanTrialToPay',
        sizeModal: 'xl',
        params: {
          arrCssFile: true,
          // Filter plans to standard+ and pre-select proPlus (matches scio1 dashboard.js)
          planFeature: 'proPlus',
          minPlanRequired: 'standard',
          // Wizard-only signal: highlight Pro Plus on the plan-comparison step.
          // Distinct from `planFeature` (which is overloaded by feature-gate callers
          // to echo the user's current plan) — opt-in only.
          recommendedPlan: 'proPlus',
        },
      });
    } finally {
      setUpgrading(false);
    }
  };

  if (bannerType === 'trial') {
    return (
      <div className="hidden sm:block bg-muted pb-2">
        <div
          className="relative flex h-12 w-full items-center justify-between border-y border-banner-border bg-banner-bg px-6 text-sm text-banner-text dark:border-primary/30 dark:text-banner-text-dark"
          role="alert"
          onClick={handleBannerClick}
        >
          <span className="relative z-10 shrink-0 truncate font-semibold">
            {t('waitingForTrial', { getDayTrial: daysLeft })}
          </span>
          {bannerHtml && (
            <SafeHtml
              html={splitPromoLink(t(bannerHtml))}
              as="span"
              className="hidden lg:inline-block absolute left-1/2 -translate-x-1/2 max-w-[50%] truncate text-center text-banner-text [&_a]:text-banner-link [&_a]:no-underline [&_a]:hover:text-banner-link-hover [&_a]:hover:underline"
            />
          )}
          {showUpgrade && <UpgradeButton upgrading={upgrading} onClick={handleUpgrade} />}
        </div>
      </div>
    );
  }

  return (
    <div className="hidden sm:block bg-muted pb-2">
      <div
        className="relative flex h-12 w-full items-center justify-between gap-3 border-y border-banner-border bg-banner-bg px-6 text-sm font-medium text-banner-text [&_a]:text-banner-link [&_a]:no-underline [&_a]:hover:text-banner-link-hover [&_a]:hover:underline [&_p]:mb-0 [&_p]:text-sm dark:border-primary/30 dark:text-banner-text-dark dark:[&_a]:text-banner-text-dark"
        role="alert"
        onClick={handleBannerClick}
      >
        <SafeHtml
          html={t(bannerHtml ?? '')}
          as="span"
          className="absolute left-1/2 -translate-x-1/2 max-w-[60%] truncate text-center"
        />
        {/* Empty left side — promo is absolutely centered in the banner */}
        <span className="shrink-0" />

        {/* Upgrade button — free plan (admin only) */}
        {showUpgrade && (
          <UpgradeButton
            upgrading={upgrading}
            onClick={(e) => {
              e.stopPropagation();
              handleUpgrade();
            }}
          />
        )}

        {/* Close button — paid users only, never for trial/free */}
        {bannerType === 'paid' && (
          <Button
            data-testid="dismiss-banner-btn"
            variant="ghost"
            size="icon-sm"
            className="-mr-3 shrink-0 rounded-full p-1 text-banner-text transition-colors hover:bg-primary/10"
            onClick={(e) => {
              e.stopPropagation();
              dismiss();
            }}
            aria-label={t('Dismiss banner')}
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        )}
      </div>
    </div>
  );
}
